/**
 * Micro-interactions. The page reads and works without any of this: content
 * is only hidden for a reveal once `.motion` is on <html>, and none of it runs
 * for visitors who ask for reduced motion. Pricing toggle and copy button work
 * either way; they just skip the animation.
 */

const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
const root = document.documentElement;
if (!reduced) root.classList.add("motion");

const ease = (t: number) => 1 - Math.pow(1 - t, 3);

// ── Header: shadow once the page moves, reading progress, current section ──
const header = document.querySelector<HTMLElement>(".site-header");
const progress = document.querySelector<HTMLElement>(".progress");
let ticking = false;

function onScroll(): void {
  ticking = false;
  const y = scrollY;
  header?.classList.toggle("is-scrolled", y > 8);
  const max = root.scrollHeight - innerHeight;
  progress?.style.setProperty("--p", String(max > 0 ? Math.min(1, y / max) : 0));
}

addEventListener(
  "scroll",
  () => {
    if (!ticking) requestAnimationFrame(onScroll);
    ticking = true;
  },
  { passive: true },
);
onScroll();

const navLinks = [...document.querySelectorAll<HTMLAnchorElement>(".site-header .nav-link")];
const spy = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      for (const link of navLinks) {
        const current = link.hash === `#${entry.target.id}`;
        link.classList.toggle("is-active", current);
        if (current) link.setAttribute("aria-current", "location");
        else link.removeAttribute("aria-current");
      }
    }
  },
  { rootMargin: "-45% 0px -50% 0px" },
);
for (const link of navLinks) {
  const section = link.hash && document.querySelector(link.hash);
  if (section) spy.observe(section);
}

// ── What it saves: cards stack into a database as you scroll ────────────────
// The section pins. Scrolling picks the step; each step then plays as a whole
// animation with its own timing, so nothing is ever left half-folded when the
// reader stops scrolling. Per card, one value runs 0 → 1 → 2: waiting, being
// read, stacked. Folding flies the card on an arc to its disc's spot while it
// tips slightly and reshapes into the disc's exact silhouette (CSS, via --f);
// the real disc then takes over in place and lands with a small settle. The
// next card rises in just after. Scrolling back plays it all in reverse.
const saves = document.querySelector<HTMLElement>(".saves");
const roomy = matchMedia("(min-height: 34rem)");
const stacking = () => !reduced && roomy.matches;

if (saves) setUpStacking(saves);

function setUpStacking(section: HTMLElement): void {
  const cards = [...section.querySelectorAll<HTMLElement>(".saving")];
  const layers = [...section.querySelectorAll<HTMLElement>(".db-layer")];
  const track = section.querySelector<HTMLElement>(".saves-track");
  const summary = section.querySelector<HTMLElement>(".saves-summary");
  if (!track || cards.length !== layers.length) return;
  const count = cards.length;

  // A card folds once the scroll is this far into its stretch.
  const FOLD_AT = 0.55;
  // Scroll units after the last card, holding on the finished stack.
  const TAIL = 0.6;
  // Milliseconds per unit of animation: rising in, and folding into the disc.
  const ENTER_MS = 620;
  const FOLD_MS = 1050;
  // The next card waits this long after the one before starts folding.
  const STAGGER_MS = 380;

  const clamp = (n: number) => Math.min(1, Math.max(0, n));
  const inOut = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
  const smooth = (x: number) => x * x * (3 - 2 * x);

  interface Tween {
    value: number;
    from: number;
    to: number;
    start: number;
    duration: number;
  }
  const tweens: Tween[] = cards.map(() => ({ value: 0, from: 0, to: 0, start: 0, duration: 0 }));
  const outro: Tween = { value: 0, from: 0, to: 0, start: 0, duration: 0 };

  let paths: { x: number; y: number; sx: number; sy: number }[] = [];
  let queued = false;

  // Only touch the DOM when a value changes: unchanged frames cost nothing.
  const written = new WeakMap<HTMLElement, string>();
  function set(el: HTMLElement, props: Record<string, string>): void {
    const value = JSON.stringify(props);
    if (written.get(el) === value) return;
    written.set(el, value);
    for (const [key, v] of Object.entries(props)) el.style.setProperty(key, v);
  }

  function measure(): void {
    for (const el of cards) {
      el.style.transform = "";
      written.delete(el);
    }
    paths = cards.map((card, i) => {
      const from = card.getBoundingClientRect();
      const to = layers[i]!.querySelector("svg")!.getBoundingClientRect();
      return {
        x: to.left + to.width / 2 - (from.left + from.width / 2),
        y: to.top + to.height / 2 - (from.top + from.height / 2),
        sx: to.width / from.width,
        sy: to.height / from.height,
      };
    });
  }

  function scrolled(): number {
    const rect = track!.getBoundingClientRect();
    const travel = rect.height - innerHeight;
    return clamp(-rect.top / (travel > 0 ? travel : 1)) * (count + TAIL);
  }

  function retarget(tween: Tween, to: number, now: number, delay = 0): void {
    if (tween.to === to) return;
    const span = Math.abs(to - tween.value);
    const fold = Math.max(0, Math.max(to, tween.value) - Math.max(1, Math.min(to, tween.value)));
    tween.from = tween.value;
    tween.to = to;
    tween.start = now + delay;
    tween.duration = Math.max(280, fold * FOLD_MS + (span - fold) * ENTER_MS);
  }

  // Which step the scroll is on: how many cards are stacked, which one is read.
  function steer(now: number): void {
    const p = scrolled();
    const stacked = cards.filter((_, i) => p > i + FOLD_AT).length;
    cards.forEach((_, i) => {
      const to = i < stacked ? 2 : i === stacked ? 1 : 0;
      const rising = to === 1 && i > 0 && tweens[i - 1]!.to === 2 && tweens[i]!.value < 0.01;
      retarget(tweens[i]!, to, now, rising ? STAGGER_MS : 0);
    });
    retarget(outro, stacked === count ? 1 : 0, now, stacked === count ? FOLD_MS * 0.7 : 0);
  }

  function advance(tween: Tween, now: number): boolean {
    if (tween.value === tween.to) return false;
    const u = clamp((now - tween.start) / tween.duration);
    tween.value = u >= 1 ? tween.to : tween.from + (tween.to - tween.from) * inOut(u);
    return true;
  }

  function render(): void {
    let current = -1;
    cards.forEach((card, i) => {
      const v = tweens[i]!.value;
      const enter = clamp(v);
      const fold = clamp(v - 1);
      // Flies and reshapes over most of the fold, then rests on its disc.
      const f = clamp(fold / 0.84);
      const p = paths[i]!;
      const arc = Math.sin(Math.PI * f);
      const x = p.x * f;
      const y = p.y * f - 30 * arc + 36 * (1 - enter);
      // Shrink evenly to the disc's width while flying (text never squashes),
      // then flatten into the disc's height as it lands.
      const rise = 0.97 + 0.03 * enter;
      const width = 1 + (p.sx - 1) * smooth(clamp(f / 0.7));
      const flat = 1 + (p.sy / p.sx - 1) * smooth(clamp((f - 0.5) / 0.5));
      const sx = width * rise;
      const sy = width * flat * rise;
      // Card and disc now share one outline in one place: hand over while still.
      const handover = clamp((fold - 0.86) / 0.14);
      set(card, {
        opacity: (enter * (1 - handover)).toFixed(3),
        transform:
          `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) perspective(900px) ` +
          `rotateX(${(10 * arc).toFixed(2)}deg) scale(${sx.toFixed(4)}, ${sy.toFixed(4)})`,
        "--f": f.toFixed(4),
      });
      card.classList.toggle("is-current", enter > 0.5 && fold < 0.2);
      if (tweens[i]!.to === 1) current = i;

      const layer = layers[i]!;
      const was = layer.dataset.landed === "1";
      set(layer, { opacity: handover.toFixed(3) });
      if (handover >= 1 && !was) {
        layer.dataset.landed = "1";
        // Settle only when it lands going forward, not when scrolling back.
        if (tweens[i]!.to === 2) {
          layer.classList.remove("is-landing");
          void layer.offsetWidth;
          layer.classList.add("is-landing");
        }
      } else if (handover < 1 && was) {
        delete layer.dataset.landed;
      }
    });
    layers.forEach((layer, i) => layer.classList.toggle("is-current", i === current));
    if (summary) {
      const done = outro.value;
      set(summary, {
        opacity: done.toFixed(3),
        transform: `translate3d(0, ${(24 * (1 - done)).toFixed(2)}px, 0)`,
      });
    }
  }

  function frame(now: number): void {
    queued = false;
    let moving = false;
    for (const tween of [...tweens, outro]) moving = advance(tween, now) || moving;
    render();
    if (moving) request();
  }

  const request = () => {
    if (!queued) requestAnimationFrame(frame);
    queued = true;
  };

  function onScroll(): void {
    if (!stacking()) return;
    steer(performance.now());
    request();
  }

  // Jump straight to the state for the current scroll position, no animation.
  function snap(): void {
    steer(performance.now());
    for (const tween of [...tweens, outro]) tween.value = tween.to;
    render();
    for (const layer of layers) layer.classList.remove("is-landing");
  }

  function apply(): void {
    const on = stacking();
    section.classList.toggle("is-stacking", on);
    if (on) {
      // Scroll position drives these now, not the one-off reveal.
      for (const card of cards) {
        card.removeAttribute("data-reveal");
        card.classList.remove("is-in");
      }
      measure();
      snap();
    } else {
      for (const el of [...cards, ...layers]) {
        el.removeAttribute("style");
        el.classList.remove("is-current", "is-landing");
        delete el.dataset.landed;
        written.delete(el);
      }
      if (summary) {
        summary.removeAttribute("style");
        written.delete(summary);
      }
      layers.forEach((layer, i) => layer.style.setProperty("--i", String(i)));
    }
  }

  for (const layer of layers) {
    layer.addEventListener("animationend", () => layer.classList.remove("is-landing"));
  }

  apply();
  roomy.addEventListener("change", apply);
  // Phones resize as the address bar slides; only re-measure on real changes.
  let width = innerWidth;
  let height = innerHeight;
  addEventListener("resize", () => {
    if (!stacking()) return;
    if (innerWidth !== width || Math.abs(innerHeight - height) > 120) {
      width = innerWidth;
      height = innerHeight;
      measure();
      render();
    }
    onScroll();
  });
  addEventListener("scroll", onScroll, { passive: true });
}

// ── Scroll reveals, staggered within each group ─────────────────────────────
const REVEAL_GROUPS = [
  ".measure",
  "main section:not(.hero) h2",
  ".section-lede",
  ...(stacking() ? [] : [".saves-grid > article"]),
  ".steps > li",
  ".fix-panel",
  ".matrix tbody tr",
  ".why-table tbody tr",
  ".rules-cards > .rule",
  ".weigh",
  ".rules-note",
  ".billing",
  ".plans > .plan",
  ".pricing-note",
  ".closing-grid > *",
  ".footer-brand",
  ".footer-cols > div",
  ".footer-sign",
];

if (!reduced) {
  const reveal = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target as HTMLElement;
        reveal.unobserve(el);
        el.classList.add("is-in");
        // Once it has arrived, drop the reveal styles so hover transitions
        // aren't held back by the stagger delay.
        const done = (event: TransitionEvent) => {
          if (event.target !== el || event.propertyName !== "opacity") return;
          el.removeEventListener("transitionend", done);
          el.removeAttribute("data-reveal");
          el.classList.remove("is-in");
        };
        el.addEventListener("transitionend", done);
      }
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.12 },
  );
  for (const selector of REVEAL_GROUPS) {
    document.querySelectorAll<HTMLElement>(selector).forEach((el, i) => {
      el.dataset.reveal = "";
      el.style.setProperty("--d", String(i));
      reveal.observe(el);
    });
  }
}

// ── Segmented switches: a pill that slides to the pressed option ────────────
function placeThumb(group: HTMLElement): void {
  const thumb = group.querySelector<HTMLElement>(".seg-thumb");
  const pressed = group.querySelector<HTMLElement>('button[aria-pressed="true"]');
  if (!thumb || !pressed) return;
  thumb.style.setProperty("--x", `${pressed.offsetLeft}px`);
  thumb.style.setProperty("--w", `${pressed.offsetWidth}px`);
}

if (!reduced) {
  const groups = [...document.querySelectorAll<HTMLElement>(".segmented")];
  const resized = new ResizeObserver((entries) => {
    for (const entry of entries) placeThumb(entry.target.closest<HTMLElement>(".segmented")!);
  });
  const pressedChanged = new MutationObserver((records) => {
    for (const record of records) placeThumb((record.target as HTMLElement).closest(".segmented")!);
  });
  for (const group of groups) {
    const thumb = document.createElement("span");
    thumb.className = "seg-thumb";
    thumb.setAttribute("aria-hidden", "true");
    group.prepend(thumb);
    group.classList.add("has-thumb");
    placeThumb(group);
    for (const button of group.querySelectorAll("button")) {
      resized.observe(button);
      pressedChanged.observe(button, { attributes: true, attributeFilter: ["aria-pressed"] });
    }
  }
  // Slide only after the first placement, so the pill doesn't fly in on load.
  requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add("thumbs-ready")));
}

// ── Stack switch: replay the code, warning and fix for the new stack ────────
function replay(el: Element | null): void {
  if (!el) return;
  el.classList.remove("is-swapping");
  void (el as HTMLElement).offsetWidth;
  el.classList.add("is-swapping");
}

document.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-orm], [data-db]");
  if (!button || reduced || button.getAttribute("aria-pressed") === "true") return;
  // main.ts re-renders in its own click handler; animate what it renders.
  requestAnimationFrame(() => {
    for (const box of document.querySelectorAll(".editor, .snippet")) {
      box.querySelectorAll<HTMLElement>(".line").forEach((line, i) => {
        line.style.setProperty("--i", String(i));
      });
    }
    replay(document.querySelector(".editor"));
    replay(document.querySelector(".fix-panel"));
  });
});

// ── Measured result: count to the real numbers once it's on screen ──────────
function countTo(el: HTMLElement, from: number, to: number, ms: number, delay = 0): void {
  const final = el.textContent ?? "";
  const format = (n: number) => (n >= 10 ? String(Math.round(n)) : n.toFixed(1));
  el.textContent = final.replace(/[\d.]+/, format(from));
  setTimeout(() => {
    const start = performance.now();
    const frame = (now: number) => {
      // A frame timestamp can predate start; clamp so the count never overshoots.
      const t = Math.min(1, Math.max(0, (now - start) / ms));
      if (t < 1) {
        el.textContent = final.replace(/[\d.]+/, format(from + (to - from) * ease(t)));
        requestAnimationFrame(frame);
      } else {
        el.textContent = final;
      }
    };
    requestAnimationFrame(frame);
  }, delay);
}

const measure = document.querySelector<HTMLElement>(".measure");
if (measure && !reduced) {
  const [before, after] = measure.querySelectorAll<HTMLElement>(".measure-row strong");
  const seen = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) return;
      seen.disconnect();
      if (before) countTo(before, 0, 300, 1100);
      if (after) countTo(after, 300, 0.2, 1300, 700);
      measure.classList.add("is-counted");
    },
    { threshold: 0.5 },
  );
  seen.observe(measure);
}

// ── Pricing: monthly / yearly ───────────────────────────────────────────────
function swapText(el: HTMLElement, text: string): void {
  if (el.textContent?.trim() === text) return;
  if (reduced || !el.animate) {
    el.textContent = text;
    return;
  }
  const out = el.animate(
    [
      { transform: "none", opacity: 1 },
      { transform: "translateY(-35%)", opacity: 0 },
    ],
    { duration: 140, easing: "ease-in" },
  );
  out.onfinish = () => {
    el.textContent = text;
    el.animate(
      [
        { transform: "translateY(35%)", opacity: 0 },
        { transform: "none", opacity: 1 },
      ],
      { duration: 260, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" },
    );
  };
}

document.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-billing]");
  if (!button) return;
  const period = button.dataset.billing === "yearly" ? "yearly" : "monthly";
  for (const b of document.querySelectorAll("[data-billing]")) {
    b.setAttribute("aria-pressed", String(b === button));
  }
  for (const el of document.querySelectorAll<HTMLElement>("[data-price-monthly]")) {
    swapText(el, el.dataset[period === "yearly" ? "priceYearly" : "priceMonthly"] ?? "");
  }
  for (const el of document.querySelectorAll<HTMLElement>("[data-for-monthly]")) {
    swapText(el, el.dataset[period === "yearly" ? "forYearly" : "forMonthly"] ?? "");
  }
});

// ── Plan cards: a soft light that follows the pointer ───────────────────────
if (!reduced) {
  for (const plan of document.querySelectorAll<HTMLElement>(".plan")) {
    plan.addEventListener("pointermove", (event) => {
      const box = plan.getBoundingClientRect();
      plan.style.setProperty("--mx", `${event.clientX - box.left}px`);
      plan.style.setProperty("--my", `${event.clientY - box.top}px`);
    });
  }
}

// ── Copy the migration SQL ──────────────────────────────────────────────────
document.addEventListener("click", async (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-copy]");
  if (!button) return;
  const figure = button.closest("figure");
  const source = figure?.querySelector(button.dataset.copy ?? "");
  const label = button.querySelector<HTMLElement>("[data-copy-label]");
  const status = figure?.querySelector<HTMLElement>("[data-copy-status]");
  if (!source || !label) return;
  let message = "Copied";
  try {
    await navigator.clipboard.writeText(source.textContent ?? "");
  } catch {
    message = /Mac|iPhone|iPad/.test(navigator.userAgent) ? "Press ⌘C" : "Press Ctrl+C";
    getSelection()?.selectAllChildren(source);
  }
  label.textContent = message;
  button.classList.add("is-done");
  if (status) status.textContent = message === "Copied" ? "SQL copied to clipboard" : "";
  clearTimeout(Number(button.dataset.timer));
  button.dataset.timer = String(
    setTimeout(() => {
      label.textContent = "Copy";
      button.classList.remove("is-done");
      if (status) status.textContent = "";
    }, 1600),
  );
});

// ── Theme switch: the new icon turns in ─────────────────────────────────────
document.addEventListener("click", (event) => {
  const toggle = (event.target as HTMLElement).closest("[data-theme-toggle]");
  if (!toggle || reduced) return;
  requestAnimationFrame(() => {
    const icon = [...toggle.querySelectorAll("svg")].find((svg) => svg.checkVisibility?.() ?? true);
    icon?.animate(
      [
        { transform: "rotate(-90deg) scale(0.6)", opacity: 0 },
        { transform: "none", opacity: 1 },
      ],
      { duration: 360, easing: "cubic-bezier(0.2, 0.8, 0.2, 1.2)" },
    );
  });
});
